import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack} from '@mui/material';
import {DateTimePicker} from '@mui/x-date-pickers/DateTimePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFnsV3';
import {ru} from 'date-fns/locale';
import {get} from 'lodash';
import {forwardRef, useImperativeHandle, useState} from 'react';
import {useForm} from 'react-hook-form';
import {toast} from 'react-toastify';
import {useSetRecoilState} from 'recoil';
import {IServiceRequest} from '../../../../shared/types/serviceRequest.types';
import {EMPTY_SERVICE_REQUEST, serviceRequestsQueryTrigger} from '../../../../store/service-requests/atoms';
import {useEventBus} from '../../../../hooks/useEventBus';
import {ServiceRequestService} from '../../../../api/services/serviceRequest.service';
import {getServiceRequestDto} from '../../new/utils';

interface Props {
  serviceRequest?: IServiceRequest;
}

export interface EditPlannedDateFormModalHandlers {
  handleClickOpen: () => void;
}

interface FormValues {
  plannedDate: Date | null;
}

function prepareDefaultValues(serviceRequest?: IServiceRequest): FormValues {
  const sr = serviceRequest || EMPTY_SERVICE_REQUEST;
  const iso = get(sr, 'plannedDate', null) as string | null;

  return {
    plannedDate: iso ? new Date(iso) : null,
  };
}

export const EditPlannedDateFormModal = forwardRef<EditPlannedDateFormModalHandlers, Props>(({serviceRequest}, ref) => {
  const [open, setOpen] = useState(false);
  const bus = useEventBus();
  const triggerSRQuery = useSetRecoilState(serviceRequestsQueryTrigger);
  const defaultValues = prepareDefaultValues(serviceRequest);

  const {control, handleSubmit, reset, setValue, watch} = useForm<FormValues>({
    defaultValues,
  });

  const plannedDate = watch('plannedDate');

  useImperativeHandle(ref, () => ({
    handleClickOpen: () => setOpen(true),
  }));

  const handleClose = () => {
    setOpen(false);
    reset(defaultValues);
  };

  const handleSave = handleSubmit(async (data) => {
    if (!serviceRequest) {
      handleClose();
      return;
    }

    try {
      await ServiceRequestService.update(
        getServiceRequestDto({
          ...serviceRequest,
          plannedDate: data.plannedDate ? data.plannedDate.toISOString() : null,
        }),
      );

      toast.success('Дата окончания заявки успешно обновлена', {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });

      setTimeout(() => triggerSRQuery((v) => v + 1), 1000);
    } catch (e) {
      bus?.emit('error', [e]);
    } finally {
      handleClose();
    }
  });

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Дата окончания заявки</DialogTitle>
      <DialogContent>
        <Stack mt={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
            <DateTimePicker
              label="Дата окончания заявки"
              format="dd.MM.yyyy"
              views={['year', 'month', 'day']}
              value={plannedDate}
              onChange={(value) => {
                setValue('plannedDate', value, {shouldDirty: true, shouldValidate: true});
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Отмена</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
});
